/**
 * Portfolio Split — one-click dual-batch generator.
 *
 * Runs the optimizer twice from a single slate pull with two different presets
 * (default: small_gpp + milly_maker) so the user can enter both a small-field
 * chalk-eating GPP and a Milly Maker leverage GPP without re-optimizing.
 *
 * Reuses the player_overrides (locks/excludes/exposures/projection edits) that
 * the user already set in the Optimizer via state.last_config.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CopySimple, Download, Play, ArrowsSplit, Shield, Flame, Crosshair, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { countSubstantiveTouches } from "@/lib/compliance";
import TwoTouchGate from "@/components/TwoTouchGate";

const MODE_META = {
  small: { color: "#39FF14", Icon: Shield,     label: "SMALL" },
  mid:   { color: "#FFCC00", Icon: Crosshair, label: "MID"   },
  large: { color: "#FF3B30", Icon: Flame,      label: "LARGE" },
};

function modeFromFieldSize(fs) {
  if (!fs) return "mid";
  if (fs < 5000) return "small";
  if (fs <= 50000) return "mid";
  return "large";
}

function BatchColumn({ batch, spec, onChangeSpec, presets, disabled }) {
  const preset = presets[spec.preset];
  const modeKey = batch?.strategy_mode || modeFromFieldSize(preset?.field_size);
  const modeMeta = MODE_META[modeKey] || MODE_META.mid;
  const Icon = modeMeta.Icon;
  return (
    <div
      data-testid={`split-batch-${spec.preset}`}
      className="border border-zinc-800 bg-zinc-950 p-4 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={16} weight="fill" style={{ color: modeMeta.color }} />
          <span className="text-[10px] mono tracking-widest font-bold" style={{ color: modeMeta.color }}>
            {modeMeta.label}-FIELD BATCH
          </span>
        </div>
        {batch && (
          <a
            data-testid={`split-download-${spec.preset}`}
            href={api.splitBatchExportUrl(spec.preset)}
            className="inline-flex items-center gap-1.5 text-[10px] mono tracking-widest text-[#39FF14] hover:text-white border border-[#39FF14]/40 hover:border-[#39FF14] px-2 py-1"
          >
            <Download size={12} /> CSV
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-[10px] mono tracking-widest text-zinc-500 flex flex-col gap-1">
          Preset
          <select
            data-testid={`split-preset-${spec.preset}`}
            value={spec.preset}
            disabled={disabled}
            onChange={(e) => onChangeSpec({ ...spec, preset: e.target.value })}
            className="bg-black border border-zinc-800 text-white text-xs mono px-2 py-1.5"
          >
            {Object.entries(presets).map(([k, p]) => (
              <option key={k} value={k}>{`${p.label} (${(p.field_size ?? 0).toLocaleString()})`}</option>
            ))}
          </select>
        </label>
        <label className="text-[10px] mono tracking-widest text-zinc-500 flex flex-col gap-1">
          # Lineups
          <input
            data-testid={`split-count-${spec.preset}`}
            type="number"
            min={1}
            max={500}
            value={spec.num_lineups}
            disabled={disabled}
            onChange={(e) => onChangeSpec({ ...spec, num_lineups: Math.max(1, Number(e.target.value) || 1) })}
            className="bg-black border border-zinc-800 text-white text-xs mono px-2 py-1.5"
          />
        </label>
      </div>

      {preset && (
        <p className="text-[10px] text-zinc-500 leading-relaxed">
          {preset.description}
        </p>
      )}

      {batch ? (
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-zinc-800">
          <Stat label="Generated" value={batch.generated} />
          <Stat label="Avg Proj" value={batch.avg_projection} />
          <Stat label="Avg Own" value={`${batch.avg_total_own}%`} />
          <Stat label="Avg Salary" value={`$${(batch.avg_salary || 0).toLocaleString()}`} />
          <Stat label="Field" value={batch.field_size?.toLocaleString()} />
          <Stat
            label="Stack Mix"
            value={
              batch.stack_type_counts
                ? Object.entries(batch.stack_type_counts).map(([k, v]) => `${k}:${v}`).join(" · ")
                : "—"
            }
          />
        </div>
      ) : (
        <div className="text-[10px] mono text-zinc-600 pt-3 border-t border-zinc-800">
          NOT YET GENERATED
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] mono tracking-widest text-zinc-500 uppercase">{label}</div>
      <div className="text-sm mono text-white font-bold truncate">{value ?? "—"}</div>
    </div>
  );
}

export default function PortfolioSplit() {
  const [presets, setPresets] = useState({});
  const [specs, setSpecs] = useState([
    { preset: "small_gpp", num_lineups: 20 },
    { preset: "milly_maker", num_lineups: 150 },
  ]);
  const [batches, setBatches] = useState({}); // keyed by preset
  const [busy, setBusy] = useState(false);
  const [overridesReady, setOverridesReady] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [touchCount, setTouchCount] = useState(0);
  const [gateOpen, setGateOpen] = useState(false);
  const [pool, setPool] = useState([]);

  // Open the Two-Touch Compliance Gate. Replaces the prior silent auto-inject
  // (top-2 projected players → 99% cap) that produced identical fingerprints
  // across users. Now the user picks a value sleeper + ceiling anchor + slider
  // position — three axes of variation → per-user unique portfolio fingerprint.
  const openGate = async () => {
    if (pool.length > 0) {
      setGateOpen(true);
      return;
    }
    try {
      const p = await api.getPool(500);
      // Same "first positive" rule as PlayerPool + SundayAutoMode — offseason
      // has integer-zero `market_mean_proj`, so `??` incorrectly drops rows.
      const isProjected = (row) => {
        const cands = [row.our_proj, row.projection, row.market_mean_proj, row.avg_ppg];
        return cands.some((v) => v != null && Number(v) > 0);
      };
      const rows = (p?.rows || []).filter(isProjected);
      if (rows.length < 6) {
        toast.error("Load a DK slate first — need at least 6 projected players for the gate.");
        return;
      }
      setPool(rows);
      setGateOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pool");
    }
  };

  // Mirrors backend/compliance.py::count_substantive_touches via the shared
  // @/lib/compliance helper. See that file for the authoritative rule.

  const onGateConfirm = (merged, gateInfo) => {
    setGateOpen(false);
    setOverrides(merged);
    const count = countSubstantiveTouches(merged);
    setTouchCount(count);
    setOverridesReady(count >= 2);
    toast.success(
      `Compliance gate cleared`,
      { description: `${gateInfo.sleeper.name} +15% · ${gateInfo.anchor.name} capped at ${gateInfo.ceiling}%. Split generation unlocked.` }
    );
  };

  useEffect(() => {
    api.contestPresets().then((d) => setPresets(d.presets || {})).catch(() => {});
    // Pull player_overrides from state.last_config (set by Optimizer).
    // Compliance rule lives in @/lib/compliance so it stays in sync with
    // SundayAutoMode + the backend. This bug previously counted only 4 of the
    // 6 touch types (missing min_exposure_pct + proj_override), so a user who
    // had 2 proj_overrides in Pro Mode would unlock SundayAutoMode but not
    // the Portfolio Split — a real rule-drift regression caught by testing.
    api.getLineups().then((d) => {
      const cfg = d?.config || {};
      const o = cfg.player_overrides || {};
      setOverrides(o);
      const substantive = countSubstantiveTouches(o);
      setTouchCount(substantive);
      setOverridesReady(substantive >= 2);
    }).catch(() => {});
    // Also fetch any previously-generated batches
    api.splitBatches().then((d) => {
      const m = {};
      for (const b of (d?.batches || [])) m[b.preset] = b;
      setBatches(m);
    }).catch(() => {});
  }, []);

  const generate = async () => {
    if (!overridesReady) {
      toast.error("Set at least 2 locks/excludes/exposures in the Optimizer first");
      return;
    }
    setBusy(true);
    try {
      const d = await api.optimizeSplit({
        batches: specs,
        player_overrides: overrides,
      });
      const m = {};
      for (const b of (d?.batches || [])) m[b.preset] = b;
      setBatches(m);
      toast.success(
        `Portfolio Split ready — ${d.batches.length} batches`,
        { description: d.batches.map((b) => `${b.preset_label}: ${b.generated}`).join(" · ") }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Split failed");
    } finally {
      setBusy(false);
    }
  };

  const setSpecAt = (idx, next) => {
    const copy = [...specs];
    copy[idx] = next;
    setSpecs(copy);
  };

  return (
    <section
      data-testid="portfolio-split-panel"
      className="bg-[#121212] border border-zinc-800"
    >
      <header className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ArrowsSplit size={20} weight="fill" className="text-[#39FF14]" />
          <div>
            <div className="overline">Portfolio Split</div>
            <div className="text-sm text-zinc-400">
              One-click dual-batch generator — small-field + large-field from a single slate
            </div>
          </div>
        </div>
        <button
          data-testid="portfolio-split-generate"
          onClick={generate}
          disabled={busy || !overridesReady}
          className={`inline-flex items-center gap-2 px-5 py-2.5 border-2 uppercase tracking-widest text-xs font-bold transition-colors ${
            overridesReady
              ? "bg-[#39FF14] text-black border-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40"
              : "bg-zinc-900 text-zinc-500 border-zinc-800 cursor-not-allowed"
          }`}
        >
          <Play size={14} weight="fill" />
          {busy ? "GENERATING..." : "GENERATE SPLIT PORTFOLIO"}
        </button>
      </header>

      {!overridesReady && (
        <div
          data-testid="portfolio-split-blocked"
          className="px-5 py-3 bg-[#39FF14]/[0.04] border-b border-[#39FF14]/20 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-center gap-3 min-w-0">
            <ShieldCheck size={16} className="text-[#39FF14] flex-shrink-0" weight="fill" />
            <div className="text-[11px] text-zinc-300">
              <span className="mono font-bold text-[#39FF14]">TWO-TOUCH GATE ·</span>{" "}
              Two real user choices before mass-generation. Open the gate for a 5-second,
              per-user portfolio fingerprint — every WarPaint subscriber ships lineups
              nobody else can match.
              <span className="text-zinc-500"> Currently {touchCount}/2.</span>
            </div>
          </div>
          <button
            data-testid="portfolio-split-open-gate"
            onClick={openGate}
            title="Opens the Two-Touch Gate — pick a value sleeper + a ceiling anchor with your own slider position. Each user's portfolio fingerprint is genuinely different."
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#39FF14] text-black hover:bg-[#2ee30d] uppercase tracking-widest text-[10px] font-bold mono transition-colors flex-shrink-0 shadow-[0_0_16px_-4px_#39FF14]"
          >
            <ShieldCheck size={11} weight="fill" />
            Open Two-Touch Gate
          </button>
        </div>
      )}

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {specs.map((spec, i) => (
          <BatchColumn
            // Slot identity IS the index here — specs[0] is the "small batch" slot,
            // specs[1] is the "large batch" slot. Preset within a slot can change,
            // but the slot position is what identifies the component.
            key={`split-slot-${i}`}
            spec={spec}
            batch={batches[spec.preset]}
            presets={presets}
            onChangeSpec={(next) => setSpecAt(i, next)}
            disabled={busy}
          />
        ))}
      </div>

      {gateOpen && (
        <TwoTouchGate
          pool={pool}
          existingOverrides={overrides}
          onClose={() => setGateOpen(false)}
          onConfirm={onGateConfirm}
        />
      )}
    </section>
  );
}
